import localforage from 'localforage';
import { openDialog, closeDialog } from './dialogs';
import { parseCSV, parseDateValue, weekBounds, openNextStep, csvCell, validateBackup, type Backup } from './data';
import type {
    Contact,
    ContactField,
    ContactWithId,
    ImportMapping,
    Interaction,
    InterviewQuestion,
    Profile,
    SchemaField,
    SpeechRecognitionLike,
    VisitOutcome
} from './types';

type GeminiErrorResponse = {
    error?: {
        message?: string;
    };
};

type GeminiResponse = {
    candidates?: Array<{
        content?: {
            parts?: Array<{
                text?: string;
            }>;
        };
    }>;
};

function byId<T extends HTMLElement>(id: string): T {
    const el = document.getElementById(id);
    if (!el) throw new Error(`Missing required element #${id}`);
    return el as T;
}

function optionalById<T extends HTMLElement>(id: string): T | null {
    return document.getElementById(id) as T | null;
}

function reportStorageError(): void {
    const status = document.querySelector<HTMLElement>('.is-open .operation-status') || byId<HTMLElement>('app-status');
    status.textContent = window.t('storageError');
}

let importPending = false;

function onAsyncClick(button: HTMLButtonElement, action: () => Promise<void>): void {
    button.addEventListener('click', async () => {
        if (button.disabled) return;
        button.disabled = true;
        const importing = !!button.closest('#import-step-2');
        const importControls = importing ? Array.from(byId<HTMLElement>('import-step-2')
            .querySelectorAll<HTMLButtonElement | HTMLSelectElement>('button, select'))
            .filter(control => control !== button).map(control => ({ control, disabled: control.disabled })) : [];
        if (importing) {
            importPending = true;
            byId<HTMLInputElement>('import-file').disabled = true;
            byId<HTMLButtonElement>('btn-import-toggle').disabled = true;
            importControls.forEach(({ control }) => { control.disabled = true; });
        }
        document.querySelectorAll<HTMLElement>('.operation-status').forEach(el => { el.textContent = ''; });
        try { await action(); }
        catch { reportStorageError(); }
        finally {
            button.disabled = false;
            if (importing) {
                importPending = false;
                byId<HTMLInputElement>('import-file').disabled = false;
                byId<HTMLButtonElement>('btn-import-toggle').disabled = false;
                importControls.forEach(({ control, disabled }) => { control.disabled = disabled; });
            }
        }
    });
}

function fieldLabel(field: SchemaField): string {
    return field.id === 'company' && ['Company', 'Compagnie', 'Entreprise'].includes(field.label)
        ? window.t('defaultFieldCompany') : field.label;
}

/* Human label for a cadence value — falls back to raw day count */
function cadenceLabelFor(days: number): string {
    const map: Record<number, string> = {
        7: 'cadenceWeekly', 14: 'cadenceBiweekly', 21: 'cadenceTriweekly',
        30: 'cadenceMonthly', 60: 'cadenceBimonthly', 90: 'cadenceQuarterly'
    };
    const key = map[days];
    return key ? window.t(key) : days + '\u00a0' + window.t('dueDaysUnit');
}

// Initialize localForage stores
const contactsStore     = localforage.createInstance({ name: 'contacts' });
const interactionsStore = localforage.createInstance({ name: 'interactions' });
const schemaStore       = localforage.createInstance({ name: 'schema' });
const profileStore      = localforage.createInstance({ name: 'profile' });

window.currentContactId = null;
let _schema: SchemaField[] = []; // in-memory schema cache
let _profile: Profile | null = null; // in-memory user profile cache

/* ── Locale-aware date formatting ── */
function formatDate(date: Date): string {
    return date.toLocaleDateString(window.t('locale'), {
        year: 'numeric', month: 'short', day: 'numeric'
    });
}

/* ================================================================
   SCHEMA MANAGEMENT
   Schema: [{id, label}] stored under key 'schema' in schemaStore
   ================================================================ */

async function loadSchema(): Promise<SchemaField[]> {
    let schema = await schemaStore.getItem<SchemaField[]>('schema');
    if (!schema) {
        schema = [{ id: 'company', label: window.t('defaultFieldCompany') }];
        await schemaStore.setItem('schema', schema);
    }
    _schema = schema;
    return schema;
}

async function saveSchema(schema: SchemaField[]): Promise<void> {
    _schema = schema;
    await schemaStore.setItem('schema', schema);
}

async function addSchemaField(label: string): Promise<string> {
    const id = 'f_' + Date.now() + '_' + Math.random().toString(36).substr(2, 6);
    _schema.push({ id, label });
    await saveSchema(_schema);
    return id;
}

/* ── Migrate old contacts: {name, company} → {name, fields: [{id, value}]} ── */
async function migrateOldContacts() {
    const keys = await contactsStore.keys();
    for (const id of keys) {
        const contact = await contactsStore.getItem<Contact>(id);
        if (contact && !Array.isArray(contact.fields)) {
            const fields: ContactField[] = [];
            if (contact.company) {
                fields.push({ id: 'company', value: contact.company });
            }
            await contactsStore.setItem(id, { ...contact, fields });
        }
    }
}

/* ================================================================
   DYNAMIC ADD-CONTACT FORM
   ================================================================ */

function renderAddContactForm() {
    const container = document.getElementById('contact-form-fields');
    if (!container) return;

    const values = new Map(Array.from(container.querySelectorAll<HTMLInputElement>('.field-input')).map(input => [input.dataset.fieldId, input.value]));

    /* Remove only the dynamically generated rows — preserve the static name input */
    container.querySelectorAll('.field-row, .add-field-row').forEach(el => el.remove());

    /* Render a row for each schema field */
    _schema.forEach(field => {
        const row = document.createElement('div');
        row.className = 'field-row';
        row.dataset.fieldId = field.id;

        const input = document.createElement('input');
        input.type = 'text';
        input.className = 'field-input';
        input.dataset.fieldId = field.id;
        input.value = values.get(field.id) || '';
        input.placeholder = fieldLabel(field);
        input.setAttribute('aria-label', input.placeholder);
        input.autocomplete = 'off';

        const removeBtn = document.createElement('button');
        removeBtn.type = 'button';
        removeBtn.className = 'btn-field-remove';
        removeBtn.textContent = window.t('fieldRemove');
        removeBtn.title = window.t('removeField') + ': ' + field.label;
        removeBtn.setAttribute('aria-label', removeBtn.title);
        onAsyncClick(removeBtn, async () => {
            _schema = _schema.filter(f => f.id !== field.id);
            await saveSchema(_schema);
            renderAddContactForm();
        });

        row.appendChild(input);
        row.appendChild(removeBtn);
        container.appendChild(row);
    });

    /* Add-field row */
    const addRow = document.createElement('div');
    addRow.className = 'add-field-row';

    const addInput = document.createElement('input');
    addInput.type = 'text';
    addInput.id = 'new-field-label';
    addInput.className = 'add-field-input';
    addInput.placeholder = window.t('addFieldPlaceholder');
    addInput.setAttribute('aria-label', addInput.placeholder);
    addInput.autocomplete = 'off';

    const addBtn = document.createElement('button');
    addBtn.type = 'button';
    addBtn.className = 'btn-add-field';
    addBtn.textContent = window.t('btnAddField');

    async function commitNewField() {
        const label = addInput.value.trim();
        if (!label) return;
        await addSchemaField(label);
        addInput.value = '';
        renderAddContactForm();
    }

    onAsyncClick(addBtn, commitNewField);
    addInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') { e.preventDefault(); addBtn.click(); }
    });

    addRow.appendChild(addInput);
    addRow.appendChild(addBtn);
    container.appendChild(addRow);
}

/* Expose for i18n re-render on language toggle */
window.renderAddContactForm = renderAddContactForm;

/* ================================================================
   CONTACT LIST
   ================================================================ */

/* Default visit rhythm when a contact has no explicit cadence */
const DEFAULT_CADENCE_DAYS = 21;
let rosterRender = 0;
let detailRender = 0;

function initialsOf(name: string): string {
    const parts = name.split(/\s+/).filter(Boolean);
    return parts.length >= 2
        ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
        : name.slice(0, 2).toUpperCase();
}

/* Compact row date — "23 sept." */
function formatShortDate(date: Date): string {
    return date.toLocaleDateString(window.t('locale'), { day: 'numeric', month: 'short' });
}

/* Latest visit that declared a next step defines the contact's open action */
type PlanItem =
    | { kind: 'overdue'; contactId: string; name: string; days: number }
    | { kind: 'followup'; contactId: string; name: string; nextStep: string; dueMs: number };

async function renderContactList(): Promise<void> {
    const generation = ++rosterRender;
    const keys     = await contactsStore.keys();
    const contacts = (await Promise.all(
        keys.map(async (id): Promise<ContactWithId | null> => {
            const contact = await contactsStore.getItem<Contact>(id);
            return contact ? { id, ...contact } : null;
        })
    )).filter((contact): contact is ContactWithId => contact !== null);

    const histories = new Map(await Promise.all(contacts.map(async contact =>
        [contact.id, await interactionsStore.getItem<Interaction[]>(contact.id) || []] as const)));
    if (generation !== rosterRender) return;
    const listDiv = byId<HTMLDivElement>('contact-list');
    listDiv.replaceChildren();

    const now = Date.now();
    const week = weekBounds(new Date(now));
    let visitsThisWeek = 0;
    let dueCount = 0;
    const planItems: PlanItem[] = [];

    for (const contact of contacts) {
        const interactions = histories.get(contact.id) || [];

        for (const inter of interactions) {
            const visitMs = parseDateValue(inter.date).getTime();
            if (visitMs >= week.start && visitMs <= now) visitsThisWeek++;
        }

        const last = interactions.length > 0 ? interactions[interactions.length - 1] : null;
        const lastMs = last ? parseDateValue(last.date).getTime() : null;
        const cadenceDays = contact.cadence && contact.cadence > 0 ? contact.cadence : DEFAULT_CADENCE_DAYS;
        const isDue = lastMs === null || now - lastMs > cadenceDays * 86400000;

        if (isDue) {
            dueCount++;
            planItems.push({
                kind: 'overdue',
                contactId: contact.id,
                name: contact.name,
                days: lastMs === null ? -1 : Math.floor((now - lastMs) / 86400000)
            });
        }

        const withStep = openNextStep(interactions);
        if (withStep && withStep.nextStep && withStep.nextStepDue) {
            const dueMs = parseDateValue(withStep.nextStepDue).getTime();
            if (!Number.isNaN(dueMs) && dueMs < week.end) {
                planItems.push({
                    kind: 'followup',
                    contactId: contact.id,
                    name: contact.name,
                    nextStep: withStep.nextStep,
                    dueMs
                });
            }
        }

        const div = document.createElement('div');
        div.className  = 'contact-item' + (isDue ? ' needs-attention' : '');
        div.dataset.id = contact.id;
        div.tabIndex   = 0;
        div.setAttribute('role', 'button');

        const avatar = document.createElement('div');
        avatar.className   = 'row-avatar';
        avatar.textContent = initialsOf(contact.name);
        avatar.setAttribute('aria-hidden', 'true');

        const main = document.createElement('div');
        main.className = 'contact-main';

        const nameSpan = document.createElement('span');
        nameSpan.className   = 'contact-name';
        nameSpan.textContent = contact.name;
        main.appendChild(nameSpan);

        /* First filled field — typically company/role */
        const firstField = contact.fields && contact.fields.find(f => f.value);
        if (firstField) {
            const sub = document.createElement('span');
            sub.className   = 'contact-sub';
            sub.textContent = firstField.value;
            main.appendChild(sub);
        }

        const side = document.createElement('div');
        side.className = 'contact-side';

        if (isDue) {
            const tag = document.createElement('span');
            tag.className   = 'tag-due';
            tag.textContent = lastMs === null
                ? window.t('dueTag')
                : window.t('dueTag') + ' · ' + Math.floor((now - lastMs) / 86400000) + ' ' + window.t('dueDaysUnit');
            side.appendChild(tag);
        }

        const dateSpan = document.createElement('span');
        dateSpan.className   = 'contact-date' + (!isDue && lastMs !== null ? ' is-fresh' : '');
        dateSpan.textContent = last ? formatShortDate(parseDateValue(last.date)) : window.t('never');
        side.appendChild(dateSpan);

        div.appendChild(avatar);
        div.appendChild(main);
        div.appendChild(side);
        div.addEventListener('click', () => { void showContactDetail(contact.id).catch(reportStorageError); });
        div.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                void showContactDetail(contact.id).catch(reportStorageError);
            }
        });
        listDiv.appendChild(div);
    }

    renderStatLine(contacts.length, visitsThisWeek, dueCount);
    renderPlan(planItems);
    applySearchFilter();
}

/* ── "Cette semaine" — overdue contacts + follow-ups due within 7 days ── */
function renderPlan(items: PlanItem[]): void {
    const panel = optionalById<HTMLElement>('plan-panel');
    const list  = optionalById<HTMLDivElement>('plan-list');
    if (!panel || !list) return;

    const followUps = items.filter(i => i.kind === 'followup').sort((a, b) =>
        a.kind === 'followup' && b.kind === 'followup' ? a.dueMs - b.dueMs : 0);
    const overdue = items.filter(i => i.kind === 'overdue').sort((a, b) =>
        a.kind === 'overdue' && b.kind === 'overdue' ? b.days - a.days : 0);
    const ordered: PlanItem[] = [...followUps, ...overdue];

    if (ordered.length === 0) {
        panel.style.display = 'none';
        list.replaceChildren();
        return;
    }

    panel.style.display = 'block';
    list.replaceChildren();

    const MAX_ROWS = 5;
    ordered.slice(0, MAX_ROWS).forEach(item => {
        const row = document.createElement('div');
        row.className = 'plan-row ' + (item.kind === 'overdue' ? 'plan-row--overdue' : 'plan-row--followup');
        row.tabIndex = 0;
        row.setAttribute('role', 'button');

        const main = document.createElement('div');
        main.className = 'contact-main';

        const name = document.createElement('span');
        name.className   = 'contact-name';
        name.textContent = item.name;
        main.appendChild(name);

        const side = document.createElement('div');
        side.className = 'contact-side';

        const tag = document.createElement('span');
        tag.className = 'plan-tag';
        if (item.kind === 'followup') {
            const why = document.createElement('span');
            why.className   = 'plan-why';
            why.textContent = item.nextStep;
            main.appendChild(why);
            if (item.dueMs < new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate()).getTime()) {
                tag.classList.add('plan-tag--late');
                tag.textContent = window.t('planLateTag') + ' · ' + formatShortDate(new Date(item.dueMs));
            } else {
                tag.textContent = formatShortDate(new Date(item.dueMs));
            }
        } else {
            tag.classList.add('plan-tag--due');
            tag.textContent = item.days >= 0
                ? window.t('dueTag') + ' · ' + item.days + '\u00a0' + window.t('dueDaysUnit')
                : window.t('dueTag');
        }
        side.appendChild(tag);

        row.appendChild(main);
        row.appendChild(side);
        row.addEventListener('click', () => { void showContactDetail(item.contactId).catch(reportStorageError); });
        row.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                void showContactDetail(item.contactId).catch(reportStorageError);
            }
        });
        list.appendChild(row);
    });

    if (ordered.length > MAX_ROWS) {
        const more = document.createElement('p');
        more.className   = 'plan-more';
        more.textContent = window.t('planMore').replace('{n}', String(ordered.length - MAX_ROWS));
        list.appendChild(more);
    }
}

/* ── Weekly stat line under the roster title ── */
function renderStatLine(contactCount: number, weekVisits: number, dueCount: number): void {
    const el = optionalById<HTMLDivElement>('stat-line');
    if (!el) return;
    el.replaceChildren();

    function seg(value: number, label: string, cls?: string): HTMLElement {
        const span = document.createElement('span');
        if (cls) span.className = cls;
        const b = document.createElement('b');
        b.textContent = String(value);
        span.appendChild(b);
        span.appendChild(document.createTextNode('\u00a0' + label));
        return span;
    }

    function dot(): HTMLElement {
        const d = document.createElement('span');
        d.className   = 'stat-dot';
        d.setAttribute('aria-hidden', 'true');
        return d;
    }

    el.appendChild(seg(contactCount, window.t(contactCount === 1 ? 'statContactOne' : 'statContacts')));
    el.appendChild(dot());
    el.appendChild(seg(weekVisits, window.t(weekVisits === 1 ? 'statVisitOne' : 'statVisitsWeek')));
    if (dueCount > 0) {
        el.appendChild(dot());
        el.appendChild(seg(dueCount, window.t('statDue'), 'stat-due'));
    }
}

/* ── Roster search — filters rendered rows, shows a note when nothing matches ── */
function applySearchFilter(): void {
    const input    = optionalById<HTMLInputElement>('contact-search');
    const listDiv  = optionalById<HTMLDivElement>('contact-list');
    const emptyMsg = optionalById<HTMLElement>('search-empty');
    if (!input || !listDiv) return;

    const query   = input.value.trim().toLowerCase();
    let visible = 0;

    listDiv.querySelectorAll<HTMLElement>('.contact-item').forEach(item => {
        const match = !query || (item.textContent || '').toLowerCase().includes(query);
        item.style.display = match ? '' : 'none';
        if (match) visible++;
    });

    if (emptyMsg) {
        emptyMsg.style.display = (query && visible === 0) ? 'block' : 'none';
    }
}

byId<HTMLInputElement>('contact-search').addEventListener('input', applySearchFilter);

/* ================================================================
   CONTACT DETAIL
   ================================================================ */

async function showContactDetail(contactId: string): Promise<void> {
    const generation = ++detailRender;
    window.currentContactId = contactId;
    const [contact, interactions] = await Promise.all([
        contactsStore.getItem<Contact>(contactId), interactionsStore.getItem<Interaction[]>(contactId)
    ]);
    if (!contact || generation !== detailRender) return;

    byId<HTMLElement>('detail-name').textContent = contact.name;

    /* Subtitle: first field value (typically company/role) */
    const firstField = contact.fields?.find(f => f.value);
    byId<HTMLElement>('detail-company').textContent = firstField ? firstField.value : '';

    /* Render all custom fields as labeled chips */
    const fieldsContainer = optionalById<HTMLDivElement>('detail-fields');
    if (fieldsContainer) {
        fieldsContainer.innerHTML = '';
        if (contact.fields && contact.fields.length > 0) {
            contact.fields.forEach(f => {
                if (!f.value) return;
                const schemaField = _schema.find(s => s.id === f.id);
                const label = schemaField ? fieldLabel(schemaField) : f.id;

                const chip = document.createElement('div');
                chip.className = 'detail-field-chip';

                const labelEl = document.createElement('span');
                labelEl.className   = 'detail-field-label';
                labelEl.textContent = label;

                const valueEl = document.createElement('span');
                valueEl.className   = 'detail-field-value';
                valueEl.textContent = f.value;

                chip.appendChild(labelEl);
                chip.appendChild(valueEl);
                fieldsContainer.appendChild(chip);
            });
        }

        /* Visit cadence chip — only when explicitly set */
        if (contact.cadence && contact.cadence > 0) {
            const chip = document.createElement('div');
            chip.className = 'detail-field-chip';

            const labelEl = document.createElement('span');
            labelEl.className   = 'detail-field-label';
            labelEl.textContent = window.t('cadenceLabel');

            const valueEl = document.createElement('span');
            valueEl.className   = 'detail-field-value';
            valueEl.textContent = cadenceLabelFor(contact.cadence);

            chip.appendChild(labelEl);
            chip.appendChild(valueEl);
            fieldsContainer.appendChild(chip);
        }
    }

    byId<HTMLElement>('detail-section').style.display = 'block';

    /* Timeline */
    const history = interactions || [];
    renderOpenAction(contactId, history);
    const listDiv = byId<HTMLDivElement>('interaction-list');
    listDiv.innerHTML = '';

    history.slice().reverse().forEach(inter => {
        const wrapper = document.createElement('div');
        wrapper.className = 'interaction-item';

        const head = document.createElement('div');
        head.className = 'interaction-head';

        const dateDiv = document.createElement('div');
        dateDiv.className   = 'interaction-date';
        dateDiv.textContent = formatDate(parseDateValue(inter.date));
        head.appendChild(dateDiv);

        if (inter.outcome) {
            const tag = document.createElement('span');
            tag.className   = 'interaction-outcome outcome-' + inter.outcome;
            tag.textContent = window.t('outcome' + inter.outcome.charAt(0).toUpperCase() + inter.outcome.slice(1));
            head.appendChild(tag);
        }
        wrapper.appendChild(head);

        const notesDiv = document.createElement('div');
        notesDiv.className   = 'interaction-notes';
        notesDiv.textContent = inter.notes || window.t('noNotes');
        wrapper.appendChild(notesDiv);

        if (inter.nextStep) {
            const next = document.createElement('div');
            next.className = 'interaction-next';
            const step = document.createElement('span');
            step.textContent = '→ ' + inter.nextStep;
            next.appendChild(step);
            if (inter.nextStepDue) {
                const due = document.createElement('span');
                due.className   = 'due';
                due.textContent = formatShortDate(parseDateValue(inter.nextStepDue));
                next.appendChild(due);
            }
            wrapper.appendChild(next);
        }

        listDiv.appendChild(wrapper);
    });
}

/* Expose for i18n re-render */
window.renderContactList = renderContactList;
window.showContactDetail = showContactDetail;

function renderOpenAction(contactId: string, interactions: Interaction[]): void {
    const container = byId<HTMLElement>('open-action');
    container.replaceChildren();
    const step = openNextStep(interactions);
    if (!step?.nextStep) return;
    const text = document.createElement('p');
    text.textContent = window.t('nextStepLabel') + ': ' + step.nextStep +
        (step.nextStepDue ? ' · ' + formatShortDate(parseDateValue(step.nextStepDue)) : '');
    const button = document.createElement('button');
    button.className = 'btn btn-ghost';
    button.textContent = window.t('completeNextStep');
    onAsyncClick(button, async () => {
        const history = await interactionsStore.getItem<Interaction[]>(contactId) || [];
        const latest = openNextStep(history);
        if (latest) {
            latest.nextStepCompleted = true;
            await interactionsStore.setItem(contactId, history);
        }
        await renderContactList();
        if (window.currentContactId === contactId) await showContactDetail(contactId);
    });
    container.append(text, button);
}

/* ================================================================
   SAVE NEW CONTACT
   ================================================================ */

onAsyncClick(byId<HTMLButtonElement>('save-contact'), async () => {
    const nameInput = byId<HTMLInputElement>('contact-name');
    const name = nameInput.value.trim();
    const errorEl = byId<HTMLElement>('name-error');
    if (!name) {
        errorEl.style.display = 'block';
        nameInput.classList.add('input-error');
        nameInput.focus();
        return;
    }
    errorEl.style.display = 'none';
    nameInput.classList.remove('input-error');

    const fields: ContactField[] = [];
    document.querySelectorAll<HTMLInputElement>('#contact-form-fields .field-input[data-field-id]').forEach(input => {
        const value = input.value.trim();
        const fieldId = input.dataset.fieldId;
        if (value && fieldId) fields.push({ id: fieldId, value });
    });

    const id = 'c_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    await interactionsStore.setItem(id, []);
    await contactsStore.setItem(id, { name, fields });

    byId<HTMLInputElement>('contact-name').value = '';
    document.querySelectorAll<HTMLInputElement>('#contact-form-fields .field-input[data-field-id]').forEach(input => {
        input.value = '';
    });

    await renderContactList();
});

/* ================================================================
   SHEETS — shared open/close for visit log and contact editor
   ================================================================ */

function openSheet(sheet: HTMLElement, backdrop: HTMLElement): void {
    openDialog(sheet, () => sheet === visitSheet ? closeVisitSheet() : closeSheet(sheet, backdrop), backdrop,
        byId<HTMLElement>(sheet === visitSheet ? 'log-interaction' : 'edit-contact'));
}

function closeSheet(sheet: HTMLElement, _backdrop: HTMLElement): void {
    if (sheet.id === 'edit-sheet') resetDeleteButton();
    closeDialog(sheet);
}

/* ================================================================
   LOG VISIT — in-app sheet with notes, outcome and next step
   ================================================================ */

const visitSheet    = byId<HTMLElement>('visit-sheet');
const visitBackdrop = byId<HTMLElement>('visit-backdrop');
const visitNotes    = byId<HTMLTextAreaElement>('visit-notes');
const visitNext     = byId<HTMLInputElement>('visit-next');
const visitDue      = byId<HTMLInputElement>('visit-due');
const draftHint     = byId<HTMLElement>('draft-hint');
const dictateBtn    = byId<HTMLButtonElement>('btn-dictate');

let _visitOutcome: VisitOutcome = 'neutral';
let _recognition: SpeechRecognitionLike | null = null;
let _recording = false;
let visitContactId: string | null = null;
let editContactId: string | null = null;

function draftKey(contactId: string): string {
    return 'ctx-visit-draft-' + contactId;
}

type VisitDraft = { notes?: string; nextStep?: string; due?: string; outcome?: VisitOutcome };

/* Draft survives app restarts — saved on every keystroke */
function saveDraft(): void {
    const contactId = visitContactId;
    if (!contactId) return;
    const draft = {
        notes: visitNotes.value,
        nextStep: visitNext.value,
        due: visitDue.value,
        outcome: _visitOutcome
    };
    if (!draft.notes && !draft.nextStep && !draft.due && draft.outcome === 'neutral') {
        try { localStorage.removeItem(draftKey(contactId)); } catch { reportStorageError(); }
        draftHint.classList.remove('visible');
        return;
    }
    try {
        localStorage.setItem(draftKey(contactId), JSON.stringify(draft));
        draftHint.textContent = window.t('draftHint');
    } catch {
        draftHint.textContent = window.t('draftError');
    }
    draftHint.classList.add('visible');
}

[visitNotes, visitNext, visitDue].forEach(el => el.addEventListener('input', () => {
    if (el === visitNotes && _recording) stopDictation();
    saveDraft();
}));

const outcomeButtons = Array.from(document.querySelectorAll<HTMLButtonElement>('#visit-outcome .seg-btn'));

function syncOutcomeButtons(): void {
    outcomeButtons.forEach(btn => {
        btn.setAttribute('aria-pressed', String(btn.dataset.outcome === _visitOutcome));
    });
}

outcomeButtons.forEach(btn => btn.addEventListener('click', () => {
    _visitOutcome = (btn.dataset.outcome as VisitOutcome) || 'neutral';
    syncOutcomeButtons();
    saveDraft();
}));

/* ── Dictation (Web Speech API) — shown only when supported ── */
const SpeechRecognitionCtor = window.SpeechRecognition || window.webkitSpeechRecognition;

if (SpeechRecognitionCtor) {
    dictateBtn.style.display = 'inline-flex';
}

dictateBtn.addEventListener('click', () => {
    if (!SpeechRecognitionCtor) return;
    if (_recording) {
        _recognition?.stop();
        return;
    }

    const rec = new SpeechRecognitionCtor();
    rec.lang = window.t('locale');
    rec.continuous = true;
    rec.interimResults = true;

    let base = visitNotes.value;
    rec.onresult = (event) => {
        if (_recognition !== rec || !_recording) return;
        let interim = '';
        for (let i = event.resultIndex; i < event.results.length; i++) {
            const result = event.results[i];
            if (result.isFinal) {
                base += (base && !base.endsWith(' ') ? ' ' : '') + result[0].transcript.trim();
            } else {
                interim += result[0].transcript;
            }
        }
        visitNotes.value = (base + (interim ? (base ? ' ' : '') + interim : '')).trimStart();
        saveDraft();
    };
    rec.onend = () => {
        if (_recognition !== rec) return;
        _recording = false;
        dictateBtn.classList.remove('recording');
        dictateBtn.setAttribute('aria-label', window.t('dictStart'));
    };
    rec.onerror = () => {
        if (_recognition !== rec) return;
        rec.onend?.();
        byId<HTMLElement>('dictation-status').textContent = window.t('dictError');
    };

    byId<HTMLElement>('dictation-status').textContent = '';
    _recognition = rec;
    _recording = true;
    dictateBtn.classList.add('recording');
    dictateBtn.setAttribute('aria-label', window.t('dictStop'));
    try {
        rec.start();
    } catch {
        rec.onerror?.(null);
    }
});

function stopDictation(): void {
    const rec = _recognition;
    _recognition = null;
    _recording = false;
    try { rec?.stop(); } catch { /* Recognition may already have ended. */ }
    dictateBtn.classList.remove('recording');
    dictateBtn.setAttribute('aria-label', window.t('dictStart'));
}

function closeVisitSheet(): void {
    stopDictation();
    closeSheet(visitSheet, visitBackdrop);
}

byId<HTMLButtonElement>('visit-cancel').addEventListener('click', closeVisitSheet);
byId<HTMLButtonElement>('visit-cancel-2').addEventListener('click', closeVisitSheet);

byId<HTMLButtonElement>('log-interaction').addEventListener('click', () => {
    const contactId = window.currentContactId;
    if (!contactId) return;

    visitContactId = contactId;
    byId<HTMLElement>('dictation-status').textContent = '';
    /* Restore draft, if any */
    let draft: VisitDraft | null = null;
    try {
        const raw = localStorage.getItem(draftKey(contactId));
        draft = raw ? JSON.parse(raw) as VisitDraft : null;
    } catch {
        draft = null;
    }
    visitNotes.value = draft?.notes || '';
    visitNext.value = draft?.nextStep || '';
    visitDue.value = draft?.due || '';
    _visitOutcome = draft?.outcome && ['good', 'neutral', 'tense'].includes(draft.outcome) ? draft.outcome : 'neutral';
    syncOutcomeButtons();
    draftHint.classList.toggle('visible', !!(draft && (draft.notes || draft.nextStep || draft.due || draft.outcome !== 'neutral')));

    openSheet(visitSheet, visitBackdrop);
    window.setTimeout(() => { if (visitSheet.classList.contains('is-open')) visitNotes.focus(); }, 380);
});

onAsyncClick(byId<HTMLButtonElement>('visit-save'), async () => {
    const contactId = visitContactId;
    if (!contactId) return;

    stopDictation();
    const notes = visitNotes.value.trim();
    const nextStep = visitNext.value.trim();
    const nextStepDue = visitDue.value || undefined;

    if (nextStepDue && !nextStep) {
        byId<HTMLElement>('dictation-status').textContent = window.t('nextStepRequired');
        visitNext.focus();
        return;
    }
    const interaction: Interaction = {
        date: new Date().toISOString(),
        notes,
        outcome: _visitOutcome
    };
    if (nextStep) {
        interaction.nextStep = nextStep;
        if (nextStepDue) interaction.nextStepDue = nextStepDue;
    }

    const interactions = await interactionsStore.getItem<Interaction[]>(contactId) || [];
    interactions.push(interaction);
    await interactionsStore.setItem(contactId, interactions);

    try { localStorage.removeItem(draftKey(contactId)); } catch { /* Saved visit is authoritative. */ }
    visitNotes.value = '';
    visitNext.value = '';
    visitDue.value = '';
    _visitOutcome = 'neutral';
    syncOutcomeButtons();
    draftHint.classList.remove('visible');

    closeVisitSheet();
    await renderContactList();
    await showContactDetail(contactId);
});

/* Clear the inline error as soon as the user types a name */
byId<HTMLInputElement>('contact-name').addEventListener('input', () => {
    byId<HTMLElement>('name-error').style.display = 'none';
    byId<HTMLInputElement>('contact-name').classList.remove('input-error');
});

/* ================================================================
   PREP CARD
   ================================================================ */

onAsyncClick(byId<HTMLButtonElement>('show-prep-card'), async () => {
    if (!window.currentContactId) return;
    const contact      = await contactsStore.getItem<Contact>(window.currentContactId);
    const interactions = await interactionsStore.getItem<Interaction[]>(window.currentContactId) || [];
    if (!contact) return;

    const content = byId<HTMLDivElement>('prep-content');
    content.replaceChildren();

    /* Identity line: Name · Field: Value · … */
    const identityP = document.createElement('p');

    const nameBold = document.createElement('strong');
    nameBold.textContent = contact.name;
    identityP.appendChild(nameBold);

    if (contact.fields && contact.fields.length > 0) {
        contact.fields.filter(f => f.value).forEach(f => {
            const sf = _schema.find(s => s.id === f.id);
            identityP.appendChild(document.createTextNode(' · '));
            const labelSpan = document.createElement('span');
            labelSpan.textContent = (sf ? fieldLabel(sf) : f.id) + ': ' + f.value;
            identityP.appendChild(labelSpan);
        });
    }
    content.appendChild(identityP);

    if (interactions.length === 0) {
        const p = document.createElement('p');
        p.textContent = window.t('noInteractions');
        content.appendChild(p);
    } else {
        const last = interactions[interactions.length - 1];

        const lastP = document.createElement('p');
        const lastBold = document.createElement('strong');
        lastBold.textContent = window.t('lastConversation') + ' (' + formatDate(parseDateValue(last.date)) + ') :';
        lastP.appendChild(lastBold);
        if (last.outcome) {
            const tag = document.createElement('span');
            tag.className   = 'interaction-outcome outcome-' + last.outcome;
            tag.textContent = window.t('outcome' + last.outcome.charAt(0).toUpperCase() + last.outcome.slice(1));
            lastP.appendChild(document.createTextNode(' '));
            lastP.appendChild(tag);
        }
        lastP.appendChild(document.createElement('br'));
        lastP.appendChild(document.createTextNode(last.notes || window.t('noNotes')));
        content.appendChild(lastP);

        /* Open action from the latest visit that declared one */
        const withStep = openNextStep(interactions);
        if (withStep && withStep.nextStep) {
            const stepP = document.createElement('p');
            const stepBold = document.createElement('strong');
            stepBold.textContent = window.t('nextStepLabel') + ' :';
            stepP.appendChild(stepBold);
            stepP.appendChild(document.createElement('br'));
            stepP.appendChild(document.createTextNode(
                withStep.nextStep + (withStep.nextStepDue
                    ? ' (' + formatShortDate(parseDateValue(withStep.nextStepDue)) + ')'
                    : '')
            ));
            content.appendChild(stepP);
        }

        if (interactions.length > 1) {
            const prev = interactions[interactions.length - 2];
            const prevP = document.createElement('p');
            const prevBold = document.createElement('strong');
            prevBold.textContent = window.t('previous') + ' :';
            prevP.appendChild(prevBold);
            prevP.appendChild(document.createTextNode(' ' + (prev.notes || '').substring(0, 80) + '…'));
            content.appendChild(prevP);
        }
    }

    const prep = byId<HTMLElement>('prep-card');
    openDialog(prep, () => closeDialog(prep), byId<HTMLElement>('modal-backdrop'), byId<HTMLElement>('show-prep-card'));
    byId<HTMLButtonElement>('close-prep').focus();
});

byId<HTMLButtonElement>('close-prep').addEventListener('click', () => {
    closeDialog(byId<HTMLElement>('prep-card'));
});

/* ================================================================
   IMPORT — CSV / XLSX
   ================================================================ */

function showImportPanel(): void {
    const section = byId<HTMLElement>('import-section');
    const step1   = byId<HTMLElement>('import-step-1');
    const step2   = byId<HTMLElement>('import-step-2');
    section.style.display = 'block';
    step1.style.display   = 'block';
    step2.style.display   = 'none';
    step2.innerHTML       = '';
    byId<HTMLInputElement>('import-file').value = '';
    setTimeout(() => section.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 50);
}

function hideImportPanel(): void {
    byId<HTMLElement>('import-section').style.display = 'none';
}

byId<HTMLButtonElement>('btn-import-toggle').addEventListener('click', () => {
    const section = byId<HTMLElement>('import-section');
    if (section.style.display === 'none' || !section.style.display) {
        showImportPanel();
    } else {
        hideImportPanel();
    }
});

byId<HTMLInputElement>('import-file').addEventListener('change', async (e) => {
    if (importPending) return;
    const target = e.target as HTMLInputElement;
    const file = target.files?.[0];
    if (!file) return;

    const fname = file.name.toLowerCase();

    /* Reject files over 10 MB */
    if (file.size > 10 * 1024 * 1024) return showImportError();

    if (fname.endsWith('.csv')) {
        try {
            const { headers, rows } = parseCSV(await file.text());
            if (headers.length === 0) return showImportError();
            renderImportStep2(headers, rows);
        } catch { showImportError(); }
    } else if (fname.endsWith('.json')) {
        try { previewBackup(validateBackup(JSON.parse(await file.text()))); }
        catch { showImportError(); }
    } else if (fname.endsWith('.xlsx') || fname.endsWith('.xls')) {
        try {
            const xlsx = await import('xlsx');
            const buffer = await file.arrayBuffer();
            const wb     = xlsx.read(buffer, { type: 'array' });
            const ws     = wb.Sheets[wb.SheetNames[0]];
            const data   = xlsx.utils.sheet_to_json<unknown[]>(ws, { header: 1, defval: '' });
            if (data.length < 2) return showImportError();
            const headers = data[0].map(String);
            const rows    = data.slice(1).map(r => r.map(String));
            renderImportStep2(headers, rows);
        } catch {
            showImportError();
        }
    }
});

function showImportError(): void {
    const step2 = byId<HTMLElement>('import-step-2');
    byId<HTMLElement>('import-step-1').style.display = 'none';
    step2.replaceChildren();
    step2.style.display = 'block';
    const p = document.createElement('p');
    p.className   = 'import-error';
    p.textContent = window.t('importError');
    step2.appendChild(p);
}

function previewBackup(backup: Backup): void {
    byId<HTMLElement>('import-step-1').style.display = 'none';
    const panel = byId<HTMLElement>('import-step-2');
    panel.style.display = 'block';
    panel.replaceChildren();
    const description = document.createElement('p');
    description.textContent = window.t('restoreHint').replace('{n}', String(backup.contacts.length));
    const button = document.createElement('button');
    button.className = 'btn btn-save';
    button.textContent = window.t('restoreBackup');
    onAsyncClick(button, async () => {
        // Merge schema IDs without changing the meaning of existing fields.
        const schema = [..._schema];
        const fieldMap = new Map<string, string>();
        for (const field of backup.schema) {
            const existing = schema.find(item => item.id === field.id);
            const id = existing && existing.label !== field.label ? 'f_' + crypto.randomUUID() : field.id;
            fieldMap.set(field.id, id);
            if (!schema.some(item => item.id === id)) schema.push({ id, label: field.label });
        }
        await saveSchema(schema);
        let count = 0;
        for (const contact of backup.contacts) {
            const fields = contact.fields!.map(field => ({ ...field, id: fieldMap.get(field.id) || field.id }));
            const history = [...backup.interactions[contact.id]].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
            const existing = await contactsStore.getItem<Contact>(contact.id);
            const existingHistory = await interactionsStore.getItem<Interaction[]>(contact.id) || [];
            const { id: originalId, ...data } = { ...contact, fields };
            if (existing && JSON.stringify(existing) === JSON.stringify(data) && JSON.stringify(existingHistory) === JSON.stringify(history)) continue;
            const id = existing ? 'c_' + crypto.randomUUID() : originalId;
            await interactionsStore.setItem(id, history);
            await contactsStore.setItem(id, data);
            count++;
        }
        if (!_profile && backup.profile) await saveProfile(backup.profile);
        renderAddContactForm();
        await renderContactList();
        panel.replaceChildren();
        const result = document.createElement('p');
        result.className = 'import-success';
        result.textContent = window.t('restoreSuccess').replace('{n}', String(count));
        panel.appendChild(result);
    });
    const cancel = document.createElement('button');
    cancel.className = 'btn btn-ghost';
    cancel.textContent = window.t('btnCancel');
    cancel.addEventListener('click', showImportPanel);
    panel.append(description, button, cancel);
}

/* ── Manual CSV parser (handles quoted fields, commas within quotes) ── */
/* ── Column mapping UI ── */
function renderImportStep2(headers: string[], rows: string[][]): void {
    byId<HTMLElement>('import-step-1').style.display = 'none';
    const step2 = byId<HTMLDivElement>('import-step-2');
    step2.style.display = 'block';
    step2.innerHTML = '';

    const hint = document.createElement('p');
    hint.className   = 'import-map-hint';
    hint.textContent = window.t('importMapHint');
    step2.appendChild(hint);

    const mappings: ImportMapping[] = []; /* [{colIndex, header, selectEl}] */

    headers.forEach((header, colIndex) => {
        const row = document.createElement('div');
        row.className = 'import-map-row';

        const colLabel = document.createElement('span');
        colLabel.className   = 'import-col-label';
        colLabel.textContent = header;

        const arrow = document.createElement('span');
        arrow.className   = 'import-arrow';
        arrow.textContent = '→';

        const select = document.createElement('select');
        select.setAttribute('aria-label', header);
        select.className = 'import-map-select';

        /* Skip option */
        const skipOpt = document.createElement('option');
        skipOpt.value       = '__skip__';
        skipOpt.textContent = window.t('importSkip');
        select.appendChild(skipOpt);

        /* Name option */
        const nameOpt = document.createElement('option');
        nameOpt.value       = '__name__';
        nameOpt.textContent = window.t('importNameCol');
        select.appendChild(nameOpt);

        /* Existing schema fields */
        _schema.forEach(field => {
            const opt = document.createElement('option');
            opt.value       = field.id;
            opt.textContent = fieldLabel(field);
            select.appendChild(opt);
        });

        /* Create new field option */
        const newOpt = document.createElement('option');
        newOpt.value       = '__new__';
        newOpt.textContent = window.t('importNewField') + header;
        select.appendChild(newOpt);

        /* Auto-detect: name column */
        const h = header.toLowerCase()
            .replace(/[éèê]/g, 'e')
            .replace(/[àâ]/g, 'a')
            .replace(/\s+/g, '');
        if (['name', 'nom', 'prenom', 'fullname', 'nomcomplet', 'contact'].includes(h)) {
            select.value = '__name__';
        }

        /* Auto-match existing schema field by label */
        if (select.value === '__skip__') {
            const match = _schema.find(f => f.label.toLowerCase() === header.toLowerCase());
            if (match) select.value = match.id;
        }

        row.appendChild(colLabel);
        row.appendChild(arrow);
        row.appendChild(select);
        step2.appendChild(row);

        mappings.push({ colIndex, header, selectEl: select });
    });

    /* Preview — first 3 rows */
    const previewCount = Math.min(3, rows.length);
    if (previewCount > 0) {
        const previewLabel = document.createElement('p');
        previewLabel.className   = 'import-preview-label';
        previewLabel.textContent = window.t('importPreview').replace('{n}', String(previewCount));
        step2.appendChild(previewLabel);

        const table = document.createElement('div');
        table.className = 'import-preview-table';

        rows.slice(0, previewCount).forEach(rowData => {
            const rowEl = document.createElement('div');
            rowEl.className = 'import-preview-row';
            rowData.forEach(cell => {
                const cellEl = document.createElement('span');
                cellEl.className   = 'import-preview-cell';
                cellEl.textContent = cell;
                rowEl.appendChild(cellEl);
            });
            table.appendChild(rowEl);
        });
        step2.appendChild(table);

        if (rows.length > 3) {
            const more = document.createElement('p');
            more.className   = 'import-more';
            more.textContent = window.t('importMore').replace('{n}', String(rows.length - 3));
            step2.appendChild(more);
        }
    }

    /* Action buttons */
    const btnRow = document.createElement('div');
    btnRow.className = 'import-btn-row';

    const cancelBtn = document.createElement('button');
    cancelBtn.className   = 'btn btn-ghost';
    cancelBtn.textContent = window.t('btnCancel');
    cancelBtn.addEventListener('click', showImportPanel);

    const importBtn = document.createElement('button');
    importBtn.className   = 'btn btn-amber';
    importBtn.textContent = window.t('btnImport');
    onAsyncClick(importBtn, () => doImport(step2, rows, mappings));

    btnRow.appendChild(cancelBtn);
    btnRow.appendChild(importBtn);
    step2.appendChild(btnRow);
}

/* ── Execute import — skips duplicates by normalized name ── */
function normalizeName(name: string): string {
    return name.toLowerCase().replace(/\s+/g, ' ').trim();
}

async function doImport(container: HTMLElement, rows: string[][], mappings: ImportMapping[]): Promise<void> {
    const nameMappings = mappings.filter(m => m.selectEl.value === '__name__');
    const nameMapping = mappings.find(m => m.selectEl.value === '__name__');
    if (!nameMapping || nameMappings.length !== 1) {
        let errEl = container.querySelector<HTMLParagraphElement>('.import-error');
        if (!errEl) { errEl = document.createElement('p'); errEl.className = 'import-error'; container.appendChild(errEl); }
        errEl.textContent = window.t('importNoName');
        return;
    }

    /* Create schema fields for any '__new__' mappings */
    const newFieldIds = new Map<number, string>();
    for (const m of mappings) {
        if (m.selectEl.value === '__new__') {
            newFieldIds.set(m.colIndex, await addSchemaField(m.header));
        }
    }

    const existingNames = new Set<string>();
    const contactKeys = await contactsStore.keys();
    for (const key of contactKeys) {
        const contact = await contactsStore.getItem<Contact>(key);
        if (contact) existingNames.add(normalizeName(contact.name));
    }

    let count = 0;
    let skipped = 0;
    for (const row of rows) {
        const name = (row[nameMapping.colIndex] || '').trim();
        if (!name) continue;

        const normalized = normalizeName(name);
        if (existingNames.has(normalized)) {
            skipped++;
            continue;
        }
        existingNames.add(normalized);

        const fields: ContactField[] = [];
        for (const m of mappings) {
            const val = m.selectEl.value;
            if (val === '__skip__' || val === '__name__') continue;
            const fieldId = val === '__new__' ? newFieldIds.get(m.colIndex) : val;
            const value   = (row[m.colIndex] || '').trim();
            if (value && fieldId) fields.push({ id: fieldId, value });
        }

        /* Unique id even for same-millisecond bulk imports */
        const id = 'c_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9) + '_' + count;
        await contactsStore.setItem(id, { name, fields });
        await interactionsStore.setItem(id, []);
        count++;
    }

    /* Show success message then close */
    container.innerHTML = '';
    const successEl = document.createElement('div');
    successEl.className   = 'import-success';
    successEl.textContent = skipped > 0
        ? window.t('importSuccessDup').replace('{n}', String(count)).replace('{m}', String(skipped))
        : window.t('importSuccess').replace('{n}', String(count));
    container.appendChild(successEl);

    renderAddContactForm();
    await renderContactList();
    // Keep the result visible until the user closes or starts another import.
}

/* ================================================================
   EDIT / DELETE CONTACT
   ================================================================ */

onAsyncClick(byId<HTMLButtonElement>('edit-contact'), openEditSheet);

function valueOfField(contact: Contact, id: string): string {
    return contact.fields?.find(f => f.id === id)?.value || '';
}

async function openEditSheet(): Promise<void> {
    const contactId = window.currentContactId;
    if (!contactId) return;
    const contact = await contactsStore.getItem<Contact>(contactId);
    if (!contact) return;
    editContactId = contactId;

    const nameInput = byId<HTMLInputElement>('edit-name');
    nameInput.value = contact.name;
    nameInput.classList.remove('input-error');
    byId<HTMLElement>('edit-name-error').style.display = 'none';

    /* Schema fields, plus any legacy fields the contact still carries */
    const container = byId<HTMLDivElement>('edit-fields');
    container.replaceChildren();
    const seen = new Set<string>();
    const addRow = (id: string, label: string, value: string) => {
        if (seen.has(id)) return;
        seen.add(id);
        const wrap = document.createElement('div');
        wrap.className = 'edit-field';

        const labelEl = document.createElement('span');
        labelEl.className   = 'field-label';
        labelEl.textContent = label;

        const input = document.createElement('input');
        input.type = 'text';
        input.className = 'field-input';
        input.dataset.fieldId = id;
        input.value = value;
        input.setAttribute('aria-label', label);
        input.autocomplete = 'off';

        wrap.appendChild(labelEl);
        wrap.appendChild(input);
        container.appendChild(wrap);
    };
    _schema.forEach(s => addRow(s.id, fieldLabel(s), valueOfField(contact, s.id)));
    (contact.fields || []).forEach(f => {
        if (!seen.has(f.id)) addRow(f.id, f.id, f.value);
    });

    const cadenceSel = byId<HTMLSelectElement>('edit-cadence');
    cadenceSel.replaceChildren();
    [
        { v: '7',  key: 'cadenceWeekly' },
        { v: '14', key: 'cadenceBiweekly' },
        { v: '21', key: 'cadenceTriweekly' },
        { v: '30', key: 'cadenceMonthly' },
        { v: '60', key: 'cadenceBimonthly' },
        { v: '90', key: 'cadenceQuarterly' }
    ].forEach(opt => {
        const option = document.createElement('option');
        option.value = opt.v;
        option.textContent = window.t(opt.key);
        cadenceSel.appendChild(option);
    });
    if (contact.cadence && !Array.from(cadenceSel.options).some(opt => Number(opt.value) === contact.cadence)) {
        cadenceSel.add(new Option(cadenceLabelFor(contact.cadence), String(contact.cadence)));
    }
    cadenceSel.value = String(contact.cadence && contact.cadence > 0 ? contact.cadence : DEFAULT_CADENCE_DAYS);

    resetDeleteButton();
    openSheet(byId<HTMLElement>('edit-sheet'), byId<HTMLElement>('edit-backdrop'));
    window.setTimeout(() => { if (byId<HTMLElement>('edit-sheet').classList.contains('is-open')) nameInput.focus(); }, 380);
}

byId<HTMLButtonElement>('edit-cancel').addEventListener('click', () => {
    closeSheet(byId<HTMLElement>('edit-sheet'), byId<HTMLElement>('edit-backdrop'));
});

onAsyncClick(byId<HTMLButtonElement>('edit-save'), async () => {
    const contactId = editContactId;
    if (!contactId) return;

    const nameInput = byId<HTMLInputElement>('edit-name');
    const name = nameInput.value.trim();
    const errorEl = byId<HTMLElement>('edit-name-error');
    if (!name) {
        errorEl.style.display = 'block';
        nameInput.classList.add('input-error');
        nameInput.focus();
        return;
    }
    errorEl.style.display = 'none';
    nameInput.classList.remove('input-error');

    const fields: ContactField[] = [];
    document.querySelectorAll<HTMLInputElement>('#edit-fields .field-input').forEach(input => {
        const value = input.value.trim();
        const fieldId = input.dataset.fieldId;
        if (value && fieldId) fields.push({ id: fieldId, value });
    });

    const cadence = parseInt(byId<HTMLSelectElement>('edit-cadence').value, 10);

    const existing = await contactsStore.getItem<Contact>(contactId);
    if (!existing) return;
    await contactsStore.setItem(contactId, { ...existing, name, fields, cadence });
    closeSheet(byId<HTMLElement>('edit-sheet'), byId<HTMLElement>('edit-backdrop'));
    await renderContactList();
    await showContactDetail(contactId);
});

byId<HTMLInputElement>('edit-name').addEventListener('input', () => {
    byId<HTMLElement>('edit-name-error').style.display = 'none';
    byId<HTMLInputElement>('edit-name').classList.remove('input-error');
});

/* ── Two-step delete — arm, then confirm within 4s ── */
const deleteBtn = byId<HTMLButtonElement>('edit-delete');
let _deleteArmed = false;
let _deleteTimer = 0;

function resetDeleteButton(): void {
    _deleteArmed = false;
    deleteBtn.classList.remove('arm');
    deleteBtn.textContent = window.t('btnDeleteContact');
    if (_deleteTimer) {
        window.clearTimeout(_deleteTimer);
        _deleteTimer = 0;
    }
}

onAsyncClick(deleteBtn, async () => {
    const contactId = editContactId;
    if (!contactId) return;

    if (!_deleteArmed) {
        _deleteArmed = true;
        deleteBtn.classList.add('arm');
        deleteBtn.textContent = window.t('btnDeleteConfirm');
        _deleteTimer = window.setTimeout(resetDeleteButton, 4000);
        return;
    }

    await contactsStore.removeItem(contactId);
    await interactionsStore.removeItem(contactId);
    localStorage.removeItem(draftKey(contactId));
    window.currentContactId = null;
    byId<HTMLElement>('detail-section').style.display = 'none';
    resetDeleteButton();
    closeSheet(byId<HTMLElement>('edit-sheet'), byId<HTMLElement>('edit-backdrop'));
    await renderContactList();
});

/* ================================================================
   EXPORT — JSON backup (full fidelity) + contacts CSV
   ================================================================ */

function downloadBlob(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function todayStamp(): string {
    return new Date().toISOString().slice(0, 10);
}

onAsyncClick(byId<HTMLButtonElement>('export-json'), async () => {
    const keys = await contactsStore.keys();
    const contacts: ContactWithId[] = [];
    const interactions: Record<string, Interaction[]> = Object.create(null);
    for (const id of keys) {
        const contact = await contactsStore.getItem<Contact>(id);
        if (!contact) continue;
        contacts.push({ id, ...contact });
        interactions[id] = await interactionsStore.getItem<Interaction[]>(id) || [];
    }
    const payload = {
        app: 'contextos',
        version: 1,
        profile: _profile,
        exportedAt: new Date().toISOString(),
        schema: _schema,
        contacts,
        interactions
    };
    downloadBlob(
        new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' }),
        'contextos-sauvegarde-' + todayStamp() + '.json'
    );
});

onAsyncClick(byId<HTMLButtonElement>('export-csv'), async () => {
    const keys = await contactsStore.keys();
    const rows: string[][] = [];
    rows.push([window.t('exportColName'), ..._schema.map(fieldLabel)]);
    for (const id of keys) {
        const contact = await contactsStore.getItem<Contact>(id);
        if (!contact) continue;
        rows.push([contact.name, ..._schema.map(s => valueOfField(contact, s.id))]);
    }
    /* BOM so Excel reads UTF-8 accents correctly */
    const csv = '\uFEFF' + rows
        .map(row => row.map(csvCell).join(','))
        .join('\r\n');
    downloadBlob(
        new Blob([csv], { type: 'text/csv;charset=utf-8' }),
        'contextos-contacts-' + todayStamp() + '.csv'
    );
});

/* ================================================================
   USER PROFILE — onboarding interview + persistent identity
   ================================================================ */

async function loadProfile(): Promise<Profile | null> {
    _profile = await profileStore.getItem<Profile>('user-profile') || null;
    return _profile;
}

async function saveProfile(data: Profile): Promise<void> {
    const profile = { ...data, savedAt: new Date().toISOString() };
    await profileStore.setItem('user-profile', profile);
    _profile = profile;
}

/* ── Interview questions (evaluated at runtime for live i18n) ── */
function getInterviewQuestions(): InterviewQuestion[] {
    return [
        { key: 'name',       q: window.t('obQ1'), ph: window.t('obQ1Ph') },
        { key: 'industry',   q: window.t('obQ2'), ph: window.t('obQ2Ph') },
        { key: 'goals',      q: window.t('obQ3'), ph: window.t('obQ3Ph') },
        { key: 'enjoys',     q: window.t('obQ4'), ph: window.t('obQ4Ph') },
        { key: 'challenges', q: window.t('obQ5'), ph: window.t('obQ5Ph') },
        { key: 'style',      q: window.t('obQ6'), ph: window.t('obQ6Ph') },
        { key: 'other',      q: window.t('obQ7'), ph: window.t('obQ7Ph') },
    ];
}

/* ── Onboarding state ── */
let _obStep = 0; // current question index (-1 = intro)
let _obAnswers: Profile = {}; // accumulated answers

function showOnboarding(editMode: boolean): void {
    const overlay = byId<HTMLElement>('onboarding-overlay');
    _obStep    = -1; // start with intro screen
    _obAnswers = editMode && _profile ? { ..._profile } : {};
    renderObStep();
    byId<HTMLButtonElement>('ob-skip-q').style.display = '';
    openDialog(overlay, hideOnboarding);
    byId<HTMLButtonElement>('ob-next').focus();
}

function hideOnboarding(): void {
    closeDialog(byId<HTMLElement>('onboarding-overlay'));
    byId<HTMLInputElement>('contact-name').focus();
}

function renderObStep(): void {
    const questions = getInterviewQuestions();
    const body      = byId<HTMLDivElement>('ob-body');
    const nextBtn   = byId<HTMLButtonElement>('ob-next');
    const skipQBtn  = byId<HTMLButtonElement>('ob-skip-q');
    const progress  = byId<HTMLDivElement>('ob-progress');

    body.replaceChildren();

    /* ── Intro screen ── */
    if (_obStep === -1) {
        const title = document.createElement('h2');
        title.id = 'ob-title-el';
        title.className   = 'ob-title';
        title.textContent = window.t('obTitle');

        const sub = document.createElement('p');
        sub.className   = 'ob-subtitle';
        sub.textContent = window.t('obSubtitle');

        body.appendChild(title);
        body.appendChild(sub);

        nextBtn.textContent  = window.t('obStart');
        skipQBtn.textContent = window.t('obSkipAll');
        progress.replaceChildren();
        return;
    }

    /* ── Question screen ── */
    if (_obStep < questions.length) {
        const q = questions[_obStep];

        const stepLabel = document.createElement('p');
        stepLabel.className   = 'ob-step-label';
        stepLabel.textContent = (_obStep + 1) + ' / ' + questions.length;

        const qEl = document.createElement('h2');
        qEl.id = 'ob-title-el';
        qEl.className   = 'ob-question';
        qEl.textContent = q.q;

        const input = document.createElement('input');
        input.type        = 'text';
        input.className   = 'ob-input';
        input.placeholder = q.ph;
        input.setAttribute('aria-labelledby', 'ob-title-el');
        input.value       = _obAnswers[q.key] || '';
        input.addEventListener('keydown', e => {
            if (e.key === 'Enter') { e.preventDefault(); obAdvance(); }
        });

        body.appendChild(stepLabel);
        body.appendChild(qEl);
        body.appendChild(input);
        setTimeout(() => { if (byId<HTMLElement>('onboarding-overlay').classList.contains('is-open')) input.focus(); }, 80);

        nextBtn.textContent  = _obStep === questions.length - 1 ? window.t('obFinish') : window.t('obNext');
        skipQBtn.textContent = window.t('obSkip');

        /* Progress dots */
        progress.replaceChildren();
        questions.forEach((_, i) => {
            const dot = document.createElement('span');
            dot.className = 'ob-dot' + (i === _obStep ? ' ob-dot--active' : (i < _obStep ? ' ob-dot--done' : ''));
            progress.appendChild(dot);
        });
        return;
    }

    /* ── Done screen ── */
    const doneEl = document.createElement('p');
    doneEl.id = 'ob-title-el';
    doneEl.className   = 'ob-done';
    doneEl.textContent = window.t('obDone');
    body.appendChild(doneEl);

    nextBtn.textContent  = '✓';
    skipQBtn.style.display = 'none';
    progress.replaceChildren();

    /* Save profile then close */
    saveProfile(_obAnswers).then(() => {
        setTimeout(hideOnboarding, 1400);
    }).catch(reportStorageError);
}

function obAdvance(): void {
    const questions = getInterviewQuestions();

    /* Collect current answer if on a question */
    if (_obStep >= 0 && _obStep < questions.length) {
        const input = document.querySelector<HTMLInputElement>('.ob-input');
        if (input) _obAnswers[questions[_obStep].key] = input.value.trim();
    }

    _obStep++;
    renderObStep();
}

/* ── Wire up onboarding buttons ── */
byId<HTMLButtonElement>('ob-next').addEventListener('click', obAdvance);

byId<HTMLButtonElement>('ob-skip-q').addEventListener('click', () => {
    if (_obStep === -1) {
        /* Skip All — mark as seen without saving anything */
        localStorage.setItem('ctx-ob-seen', '1');
        hideOnboarding();
    } else {
        /* Skip this question, advance */
        _obStep++;
        renderObStep();
    }
});

byId<HTMLButtonElement>('ob-edit').addEventListener('click', () => showOnboarding(true));

/* ================================================================
   AI INTELLIGENCE LAYER
   ================================================================ */

const GEMINI_ENDPOINT =
    'https://generativelanguage.googleapis.com/v1beta/models/gemini-3.6-flash:generateContent';

/* ── Collect all contacts + full interaction history into one object ── */
async function getAllContactsWithHistory(): Promise<Array<ContactWithId & { interactions: Interaction[] }>> {
    const keys = await contactsStore.keys();
    const contacts = await Promise.all(keys.map(async (id): Promise<(ContactWithId & { interactions: Interaction[] }) | null> => {
        const contact      = await contactsStore.getItem<Contact>(id);
        const interactions = await interactionsStore.getItem<Interaction[]>(id) || [];
        if (!contact) return null;
        return { id, ...contact, interactions };
    }));
    return contacts.filter((contact): contact is ContactWithId & { interactions: Interaction[] } => contact !== null);
}

/* ── Build the system prompt that gives the AI full context ── */
function buildSystemPrompt(contacts: Array<ContactWithId & { interactions: Interaction[] }>): string {
    const todayStr = new Date().toLocaleDateString(window.t('locale'), {
        weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
    });

    let prompt = `You are a sharp, trusted field sales assistant. Today is ${todayStr}.\n\n`;

    /* Inject user profile for personalisation */
    if (_profile) {
        const p = _profile;
        prompt += `## About the person you're helping\n`;
        if (p.name)       prompt += `- Call them: ${p.name}\n`;
        if (p.industry)   prompt += `- Industry: ${p.industry}\n`;
        if (p.goals)      prompt += `- Professional goals: ${p.goals}\n`;
        if (p.enjoys)     prompt += `- What energizes them: ${p.enjoys}\n`;
        if (p.challenges) prompt += `- What they find hard: ${p.challenges}\n`;
        if (p.style)      prompt += `- Preferred response style: ${p.style}\n`;
        if (p.other)      prompt += `- Extra context: ${p.other}\n`;
        prompt += `\nPersonalize every response to this person. Address them by their preferred name. Align your advice with their goals and what motivates them. Adapt your tone to their stated style preference. Be like a trusted colleague who genuinely knows them.\n\n`;
    }

    prompt += `The user manages ${contacts.length} professional contact(s). Here is their complete data:\n\n`;

    for (const c of contacts) {
        prompt += `### ${c.name}\n`;

        if (c.fields && c.fields.length > 0) {
            c.fields.forEach(f => {
                if (!f.value) return;
                const sf = _schema.find(s => s.id === f.id);
                prompt += `- ${sf ? fieldLabel(sf) : f.id}: ${f.value}\n`;
            });
        }

        if (c.cadence) prompt += `- Visit cadence: ${c.cadence} days\n`;
        const step = openNextStep(c.interactions);
        if (step?.nextStep) prompt += `- Open next step: ${step.nextStep}${step.nextStepDue ? ' (due ' + step.nextStepDue + ')' : ''}\n`;
        if (c.interactions.length > 0) {
            const recent = c.interactions.slice(-5);
            prompt += `- Visits logged: ${c.interactions.length} total. Last ${recent.length}:\n`;
            recent.forEach(i => {
                const daysAgo = Math.floor((Date.now() - parseDateValue(i.date).getTime()) / 86400000);
                prompt += `  • ${daysAgo} day(s) ago: ${i.notes || '(no notes)'}${i.outcome ? ' (outcome: ' + i.outcome + ')' : ''}\n`;
            });
        } else {
            prompt += `- No visits logged yet\n`;
        }
        prompt += '\n';
    }

    prompt += `\nRules: Respond in the same language the user writes in. Be specific — reference actual client names and data. Be actionable and concise. Use bullet points for lists.`;
    return prompt;
}

/* ── Optional Gemini request (explicit user action only) ── */
async function callGemini(question: string): Promise<string> {
    const apiKey = localStorage.getItem('ctx-api-key');
    if (!apiKey) throw new Error('no_key');

    const contacts = await getAllContactsWithHistory();
    if (contacts.length === 0) throw new Error('no_contacts');

    const res = await fetch(GEMINI_ENDPOINT, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', 'x-goog-api-key': apiKey },
        signal: AbortSignal.timeout(30000),
        body: JSON.stringify({
            system_instruction: { parts: [{ text: buildSystemPrompt(contacts) }] },
            contents: [{ role: 'user', parts: [{ text: question }] }],
            generationConfig: { temperature: 0.7, maxOutputTokens: 1024 }
        })
    });

    if (!res.ok) {
        const body = await res.json().catch(() => ({})) as GeminiErrorResponse;
        throw new Error(body.error?.message || 'api_error');
    }

    const data = await res.json() as GeminiResponse;
    const answer = data.candidates?.[0]?.content?.parts?.map(part => part.text || '').join('') || '';
    if (!answer.trim()) throw new Error('empty_response');
    return answer;
}

/* ── Render AI response: lightweight markdown (bold, bullets, numbered) ── */
function renderMarkdownLite(text: string, container: HTMLElement): void {
    container.replaceChildren();
    let currentList: HTMLUListElement | HTMLOListElement | null = null;

    text.split('\n').forEach(rawLine => {
        const line = rawLine.trim();
        if (!line) { currentList = null; return; }

        if (line.startsWith('- ') || line.startsWith('• ') || line.startsWith('* ')) {
            if (!currentList || currentList.tagName !== 'UL') {
                currentList = document.createElement('ul');
                currentList.className = 'ai-list';
                container.appendChild(currentList);
            }
            const li = document.createElement('li');
            renderInlineBold(li, line.slice(2));
            currentList.appendChild(li);
        } else if (/^\d+\.\s/.test(line)) {
            if (!currentList || currentList.tagName !== 'OL') {
                currentList = document.createElement('ol');
                currentList.className = 'ai-list';
                container.appendChild(currentList);
            }
            const li = document.createElement('li');
            renderInlineBold(li, line.replace(/^\d+\.\s/, ''));
            currentList.appendChild(li);
        } else {
            currentList = null;
            const p = document.createElement('p');
            renderInlineBold(p, line.replace(/^#{1,3}\s*/, '')); // strip markdown headings
            container.appendChild(p);
        }
    });
}

function renderInlineBold(el: HTMLElement, text: string): void {
    text.split(/(\*\*[^*]+\*\*)/g).forEach((part, i) => {
        if (i % 2 === 1) {
            const strong = document.createElement('strong');
            strong.textContent = part.slice(2, -2);
            el.appendChild(strong);
        } else if (part) {
            el.appendChild(document.createTextNode(part));
        }
    });
}

/* ── Render the quick-prompt chips (re-runs on language toggle) ── */
function renderQuickPrompts(): void {
    const container = optionalById<HTMLDivElement>('quick-prompts');
    if (!container) return;
    container.replaceChildren();

    [
        { label: 'quickPrompt1Label', full: 'quickPrompt1Full' },
        { label: 'quickPrompt2Label', full: 'quickPrompt2Full' },
        { label: 'quickPrompt3Label', full: 'quickPrompt3Full' },
    ].forEach(({ label, full }) => {
        const btn = document.createElement('button');
        btn.className   = 'quick-prompt-chip';
        btn.textContent = window.t(label);
        btn.addEventListener('click', () => {
            byId<HTMLInputElement>('ask-input').value = window.t(full);
            askAI();
        });
        container.appendChild(btn);
    });
}
window.renderQuickPrompts = renderQuickPrompts;

/* ── Show/hide the "key saved" placeholder ── */
function initApiKeyInput(): void {
    const input = optionalById<HTMLInputElement>('api-key-input');
    if (!input) return;
    input.value       = '';
    input.placeholder = localStorage.getItem('ctx-api-key')
        ? window.t('keyConfigured')
        : window.t('geminiKeyPh');
}
window.initApiKeyInput = initApiKeyInput;

/* ── Save API key ── */
byId<HTMLButtonElement>('save-api-key').addEventListener('click', () => {
    const input = byId<HTMLInputElement>('api-key-input');
    const val   = input.value.trim();
    if (!val) return;
    localStorage.setItem('ctx-api-key', val);
    input.value       = '';
    input.placeholder = window.t('keyConfigured');

    /* Brief amber flash on button */
    const btn = byId<HTMLButtonElement>('save-api-key');
    btn.classList.add('btn-save-key--saved');
    setTimeout(() => btn.classList.remove('btn-save-key--saved'), 1500);
});

/* ── Ask the AI ── */
let aiPending = false;
async function askAI(): Promise<void> {
    const input      = byId<HTMLInputElement>('ask-input');
    const question   = input.value.trim();
    if (!question || aiPending) return;
    aiPending = true;
    byId<HTMLButtonElement>('ask-btn').disabled = true;

    const responseArea = byId<HTMLElement>('ai-response-area');
    const responseEl   = byId<HTMLElement>('ai-response');

    responseArea.style.display = 'block';
    responseEl.replaceChildren();

    const loading = document.createElement('p');
    loading.className   = 'ai-loading';
    loading.textContent = window.t('insightLoading');
    responseEl.appendChild(loading);

    responseArea.scrollIntoView({ behavior: 'smooth', block: 'nearest' });

    try {
        const answer = await callGemini(question);
        responseEl.replaceChildren();
        renderMarkdownLite(answer, responseEl);
    } catch (err) {
        responseEl.replaceChildren();
        const errEl = document.createElement('p');
        errEl.className   = 'ai-error-msg';
        const message = err instanceof Error ? err.message : '';
        errEl.textContent = message === 'no_key'       ? window.t('insightNoKey')
                          : message === 'no_contacts'  ? window.t('insightNoContacts')
                          : window.t('insightError');
        responseEl.appendChild(errEl);
    } finally {
        aiPending = false;
        byId<HTMLButtonElement>('ask-btn').disabled = false;
    }
}

byId<HTMLButtonElement>('ask-btn').addEventListener('click', askAI);
byId<HTMLInputElement>('ask-input').addEventListener('keydown', e => {
    if (e.key === 'Enter') { e.preventDefault(); askAI(); }
});

/* ================================================================
   INITIAL LOAD
   ================================================================ */

(async function init() {
    await loadSchema();
    await migrateOldContacts();
    await loadProfile();
    renderAddContactForm();
    renderContactList();
    renderQuickPrompts();
    initApiKeyInput();

    /* Show onboarding on first-ever launch (no profile, never seen) */
    if (!_profile && !localStorage.getItem('ctx-ob-seen')) {
        setTimeout(() => showOnboarding(false), 600);
    }
    /* Mark as seen so auto-show never repeats, even if user skips */
    localStorage.setItem('ctx-ob-seen', '1');
})().catch(reportStorageError);

/* ── Service worker ── */
if (import.meta.env.PROD && 'serviceWorker' in navigator) {
    navigator.serviceWorker.register('sw.js').catch(() => {});
}
